import { type HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '@/app/core/services/auth.service';
import { HANDLE_401_LOCALLY } from './auth-request-context';

/**
 * HTTP Interceptor that automatically refreshes token on 401 errors
 * and retries the original request with new token
 */
export const tokenRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const handles401Locally = req.context.get(HANDLE_401_LOCALLY);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only handle 401 Unauthorized errors
      if (error.status !== 401) {
        return throwError(() => error);
      }

      if (handles401Locally) {
        return throwError(() => error);
      }

      // Passkey login endpoints handle 401 locally in the sign-in / setup UI
      // (invalid challenge, expired setup token) — no session to refresh yet.
      // Management endpoints (GET/PATCH/DELETE /login/passkeys[/{id}]) are
      // session-authenticated and keep the default refresh-and-retry handling.
      if (
        req.url.includes('/login/passkeys/authentication/') ||
        req.url.includes('/login/passkeys/registration/')
      ) {
        return throwError(() => error);
      }

      // Skip refresh for auth endpoints to avoid infinite loop
      const isAuthEndpoint =
        req.url.includes('/login/init') ||
        req.url.includes('/login/emailConfirm') ||
        req.url.includes('/login/phoneConfirm') ||
        req.url.includes('/login/refresh') ||
        req.url.includes('/login/whoami');

      if (isAuthEndpoint) {
        authService.logout();
        return throwError(() => error);
      }

      // Try to refresh token and retry original request
      return authService.refreshToken().pipe(
        switchMap((newToken) => {
          // Clone the original request with new token
          const clonedRequest = req.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
            },
          });
          // Retry the request with new token
          return next(clonedRequest);
        }),
        catchError((refreshError) => {
          // If refresh fails, logout user
          authService.logout();
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
